// frontend/src/pages/Login.js

import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Mail, ArrowRight, Phone } from "lucide-react";

import { useAuth } from "../context/AuthContext";
import SEO from "../components/SEO";

const Login = () => {
  const navigate = useNavigate();
  const { signIn } = useAuth(); // assumes AuthContext provides signIn(email, password)

  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ email: "", password: "" });

  const handleChange = (e) => {
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) {
      toast.error("Please enter email and password.");
      return;
    }

    try {
      setLoading(true);
      await signIn(form.email, form.password);
      toast.success("Welcome back!");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(err?.message || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F4EF]">
      {/* ✅ SEO for Login (Noindex recommended) */}
      <SEO
        title="Login | OJOVEST"
        description="Secure login to access your OJOVEST dashboard, loan applications, and premium credit card offers."
        path="/login"
        noindex={true}
      />

      {/* Header */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <Link to="/" className="inline-flex items-center gap-2 text-[#354F37] font-bold text-xl">
          OJOVEST
        </Link>
      </div>

      {/* Content */}
      <div className="max-w-lg mx-auto px-4 pb-16">
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-8">
          <h1 className="text-3xl font-bold text-[#222222] mb-2">Welcome back</h1>
          <p className="text-[#444444] mb-8">
            Login to view your dashboard and manage your requests.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-[#222222] mb-2">
                Email
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <Mail size={18} />
                </span>
                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  autoComplete="email"
                  required
                  className="w-full pl-10 pr-4 py-3 rounded-lg bg-white border border-gray-300 text-[#444444] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#354F37]"
                  placeholder="you@email.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#222222] mb-2">
                Password
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <Lock size={18} />
                </span>
                <input
                  name="password"
                  type="password"
                  value={form.password}
                  onChange={handleChange}
                  autoComplete="current-password"
                  required
                  className="w-full pl-10 pr-4 py-3 rounded-lg bg-white border border-gray-300 text-[#444444] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#354F37]"
                  placeholder="••••••••"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#354F37] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#426045] transition flex items-center justify-center visibility gap-2 disabled:opacity-60"
            >
              {loading ? "Logging in..." : "Login"}
              <ArrowRight size={18} />
            </button>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
              <p className="text-sm text-[#444444]">
                New here?{" "}
                <Link to="/register" className="text-[#354F37] font-semibold hover:underline">
                  Create account
                </Link>
              </p>

              {/* Optional: quick WhatsApp help */}
              <a
                href="https://wa.me/918651035362?text=Hi%20OJOVEST%20-%20I%20need%20help%20logging%20in."
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#354F37] hover:underline"
              >
                <Phone size={16} /> Need help on WhatsApp
              </a>
            </div>
          </form>
        </div>

        {/* Trust note */}
        <div className="mt-6 text-center text-sm text-[#444444]">
          By logging in you agree to our{" "}
          <Link to="/" className="text-[#354F37] font-semibold hover:underline">
            terms & privacy policy
          </Link>
          .
        </div>
      </div>
    </div>
  );
};

export default Login;
